import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Chip, DataGrid, Icon, IconButton, Tooltip, Typography } from '@emburse/embark-core';
import { FileDownloadIcon } from '@emburse/embark-icons';
import dayjs from 'dayjs';
import { $t, useTranslation } from '../../../hooks/useTranslation';
function ERPGatewayHistoryImported({ data, loading, canDownload, onDownloadfile, canShowDetails }) {
    useTranslation();
    const convertTime = (time, timeType) => {
        const shortTime = timeType === 'hour' ? 'minute' : 'second';
        let longTime = Math.floor(time / 60);
        let remainingTime = Math.round(parseFloat((time % 60).toString()));
        if (remainingTime === 60) {
            longTime += 1;
            remainingTime = 0;
        }
        const long = 
        // eslint-disable-next-line no-nested-ternary
        longTime === 0
            ? ''
            : longTime > 1
                ? `${longTime} ${$t(`${timeType}s`)} `
                : `${longTime} ${$t(`${timeType}`)} `;
        const short = 
        // eslint-disable-next-line no-nested-ternary
        remainingTime === 0
            ? ''
            : remainingTime > 1
                ? `${remainingTime} ${$t(`${shortTime}s`)}`
                : `${remainingTime} ${$t(`${shortTime}`)}`;
        return `${long}${short}`;
    };
    const formatDuration = (sDate, eDate) => {
        if (!sDate || !eDate) {
            return '';
        }
        const startDate = dayjs(sDate);
        const endDate = dayjs(eDate);
        let diff = endDate.diff(startDate, 'minute', true);
        if (diff < 1) {
            diff = endDate.diff(startDate, 'second', true);
            return `${diff.toFixed()} ${$t('seconds')}`;
        }
        if (diff < 60) {
            diff = endDate.diff(startDate, 'second', true);
            return convertTime(diff, 'minute');
        }
        return convertTime(diff, 'hour');
    };
    const formatStatus = (status) => (_jsxs("div", { style: { width: '100%' }, children: [status.toLowerCase().includes('failed') && (_jsx(Tooltip, { title: $t('An error occurred during the import. Please check your configuration and initiate a new import from the Schedule tab.'), placement: "left", children: _jsx(Chip, { label: $t('Failed'), color: "error", size: "mini", variant: "outlined" }) })), status.toLowerCase().includes('running') && (_jsx(Chip, { label: $t('Running'), color: "info", size: "mini", variant: "outlined" })), status.toLowerCase().includes('pending') && (_jsx(Chip, { label: $t('Pending'), color: "info", size: "mini", variant: "outlined" })), status.toLowerCase().includes('completed') && (_jsx(Chip, { label: $t('Completed'), color: "success", size: "mini", variant: "outlined" }))] }));
    const formatUserSchedule = (user) => (user ? 'Manual' : 'Scheduled');
    const downloadDisabled = (status) => status.toLowerCase().includes('failed') || status.toLowerCase().includes('completed');
    const columns = () => [
        {
            field: 'StartDate',
            headerName: $t('Date'),
            flex: 2,
            editable: true,
            renderCell: (cell) => (_jsx(Typography, { variant: "body2", children: `${dayjs(cell.value)
                    .utc(true)
                    .local()
                    .tz(dayjs.tz.guess())
                    .format('ddd L LT')}` }))
        },
        {
            field: 'EndDate',
            headerName: $t('Duration'),
            flex: 2,
            renderCell: (cell) => (_jsx(Typography, { variant: "body2", children: formatDuration(cell.row.StartDate, cell.row.EndDate) }))
        },
        {
            field: 'Config',
            headerName: $t('Type'),
            flex: 2,
            renderCell: (cell) => {
                const parsedConfig = cell.row.Config ? JSON.parse(cell.row.Config) : {};
                return (_jsx(Typography, { variant: "body2", children: formatUserSchedule(parsedConfig?.InitiatingUserId) }));
            }
        },
        {
            field: 'Status',
            headerName: $t('Status'),
            width: 110,
            renderCell: (cell) => formatStatus(cell.value)
        },
        ...(canDownload
            ? [
                {
                    field: 'ActionPlaceholder',
                    headerName: $t('Details'),
                    width: 65,
                    renderCell: (cell) => (_jsx("div", { style: {
                            width: '100%',
                            textAlign: 'center'
                        }, children: canShowDetails(cell.row.StartDate) && (_jsx(IconButton, { color: "monochrome", size: "medium", title: "Download", disabled: !downloadDisabled(cell.row.Status), onClick: () => {
                                onDownloadfile(cell.row.Id, 'import');
                            }, children: _jsx(Icon, { iconUrl: FileDownloadIcon }) })) }))
                }
            ]
            : [])
    ];
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h5", children: $t('Imported') }), _jsx(DataGrid, { autoHeight: true, pageSizeOptions: [5, 10, 100], loading: loading, localeText: {
                    noRowsLabel: $t('Import history logs will appear here once an import is complete.')
                }, rows: data, columns: columns() })] }));
}
export default ERPGatewayHistoryImported;
